/**
 * OrderDetailsView - Full-page view for order details
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { getBooking } from "../../quotes/services/bookingService";
import {
  getOrder,
  getOrderCompletionEligibility,
  updateOrderStatus,
} from "../services/orderService";
import { orderDownloadService } from "../services/downloadService";
import OrderLinkedRecordsStrip from "../components/OrderLinkedRecordsStrip.jsx";
import OrderJourneyBar from "../components/OrderJourneyBar.jsx";
import OrderCommandHeader from "../components/OrderCommandHeader.jsx";
import OrderAttentionRail from "../components/OrderAttentionRail.jsx";
import OrderMissionWorkspaces from "../components/OrderMissionWorkspaces.jsx";
import ProviderInvitationPanel from "../../providerOpportunities/components/ProviderInvitationPanel.jsx";
import ProviderSelectionPanel from "../../providerOpportunities/components/ProviderSelectionPanel.jsx";
import MarketplaceCohortOpsBanner from "../../providerOpportunities/components/MarketplaceCohortOpsBanner.jsx";
import { getQuoteById } from "../../quotes/services/quoteService.js";
import { getLead } from "../../leads/services/leadService.js";
import { getInvoices } from "../../invoices/services/invoiceService.js";
import { logError, logWarn } from "@/shared/utils/logger.js";
import { openAppPopup } from "@/shared/utils/openAppPopup.js";
import { useAuth } from "@/contexts/AuthContext.jsx";
import LinkInvoiceToOrderModal from "../components/LinkInvoiceToOrderModal.jsx";
import AddOrderChargesModal from "../components/AddOrderChargesModal.jsx";
import CompleteOrderConfirmModal from "../components/CompleteOrderConfirmModal.jsx";
import { getTrip } from "../../trips/services/tripService.js";
import { getSetting } from "../../systemSettings/services/systemSettingsService.js";
import {
  DEFAULT_HEALTH_THRESHOLDS,
} from "../services/orderOperationalStatus.js";
import {
  JobJourneyStrip,
  invoiceDisplayFrom,
  leadDisplayFrom,
  pickEntityId,
  pickPrimaryInvoice,
  pickPrimaryInvoiceId,
  quoteDisplayFrom,
  seedFromOrder,
} from "@/shared/jobJourney/index.js";

const MONEY_ROUNDING_TOLERANCE = 0.01;
const QUICK_STATUS_LABELS = {
  pending: "Mark as Pending",
  booked: "Mark as Booked",
  in_progress: "Mark as In Progress",
  arrived_at_storage: "Mark as Arrived at Storage",
  in_transit_from_storage: "Mark as In Transit from Storage",
  completed: "Mark as Completed",
  cancelled: "Mark as Cancelled",
};
const ALL_ORDER_STATUSES = [
  "pending",
  "booked",
  "in_progress",
  "arrived_at_storage",
  "in_transit_from_storage",
  "completed",
  "cancelled",
];
const NEXT_STATUS_BY_CURRENT = {
  pending: ["booked"],
  booked: ["in_progress"],
  in_progress: ["arrived_at_storage"],
  arrived_at_storage: ["in_transit_from_storage"],
  in_transit_from_storage: ["completed"],
  completed: [],
  cancelled: [],
};

const normalizeInvoices = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.invoices)) return payload.invoices;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const computeInvoiceRollup = (invoices = []) => {
  const list = Array.isArray(invoices) ? invoices : [];

  let total = 0;
  let totalPaid = 0;
  let totalRefunded = 0;
  let balance = 0;

  for (const inv of list) {
    if (inv?.status === "cancelled") {
      continue;
    }

    const invTotal = Number(inv?.total || 0);
    total += invTotal;

    const summary = inv?.paymentSummary;
    if (summary && typeof summary === "object") {
      totalPaid += Number(summary.totalPaid || 0);
      totalRefunded += Number(summary.totalRefunded || 0);
      balance += Number(summary.balance || 0);
      continue;
    }

    // Fallback: mirror backend AR rule (only completed + payment_reversal affect balance)
    const paidFromPayments = (inv?.payments || []).reduce(
      (sum, p) => sum + Number(p?.amount || 0),
      0
    );
    const refundedForBalance = (inv?.refunds || [])
      .filter((r) => r?.status === "completed")
      .filter(
        (r) =>
          (r?.classification || "payment_reversal") === "payment_reversal"
      )
      .reduce((sum, r) => sum + Number(r?.amount || 0), 0);
    const netPaid = Math.max(paidFromPayments - refundedForBalance, 0);
    const invBalance = Math.max(invTotal - netPaid, 0);

    totalPaid += netPaid;
    totalRefunded += refundedForBalance;
    balance += invBalance;
  }

  return {
    count: list.length,
    total,
    totalPaid,
    totalRefunded,
    balance,
    isClear: balance <= MONEY_ROUNDING_TOLERANCE,
  };
};

const getWhatsAppLink = (phone) => {
  if (!phone) return null;
  const cleaned = String(phone).replace(/\D/g, "");
  if (!cleaned) return null;
  if (cleaned.startsWith("27")) return `https://wa.me/${cleaned}`;
  if (cleaned.startsWith("0")) return `https://wa.me/27${cleaned.slice(1)}`;
  return `https://wa.me/${cleaned}`;
};

/**
 * OrderDetailsView Component
 */
const OrderDetailsView = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyPermission } = useAuth();
  const canLinkInvoice = hasAnyPermission(["quotes:update", "orders:update"]);
  const canManageInvoices = hasAnyPermission([
    "quotes:create",
    "quotes:update",
  ]);
  const canViewInvoices = hasAnyPermission(["quotes:view"]);

  // Get initial order data from location state (optimistic update)
  const initialOrder = location.state?.order;

  // State
  const [order, setOrder] = useState(initialOrder || null);
  const [loading, setLoading] = useState(!initialOrder); // Don't show loading if we have initial data
  const [error, setError] = useState(null);
  const [hasBooking, setHasBooking] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [quote, setQuote] = useState(null);
  const [lead, setLead] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [showOperationalFlow, setShowOperationalFlow] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [linkInvoiceModalOpen, setLinkInvoiceModalOpen] = useState(false);
  const [addChargesModalOpen, setAddChargesModalOpen] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [trip, setTrip] = useState(null);
  const [healthThresholds, setHealthThresholds] = useState(
    DEFAULT_HEALTH_THRESHOLDS
  );
  const [providerOpsRefresh, setProviderOpsRefresh] = useState(0);

  // Fetch order data and booking in parallel
  const fetchOrderData = useCallback(async () => {
    if (!orderId) return;

    try {
      // Only show loading if we don't have initial data
      if (!initialOrder) {
        setLoading(true);
      }
      setError(null);

      // Load order and booking in parallel
      const [orderData, bookingResponse] = await Promise.allSettled([
        getOrder(orderId),
        getBooking(orderId),
      ]);

      // Handle order result
      if (orderData.status === "fulfilled") {
        setOrder(orderData.value);
      } else {
        logError("Error fetching order", {
          orderId,
          error: orderData.reason?.message || String(orderData.reason),
        });
        setError(orderData.reason?.message || "Failed to load order");
      }

      // Handle booking result
      if (bookingResponse.status === "fulfilled") {
        const response = bookingResponse.value;
        setHasBooking(response.success && response.data);
      } else {
        setHasBooking(false);
      }
    } catch (err) {
      logError("Error fetching order data", {
        orderId,
        error: err?.message || String(err),
      });
      setError(err.message || "Failed to load order");
    } finally {
      setLoading(false);
    }
  }, [orderId, initialOrder]);

  useEffect(() => {
    fetchOrderData();
  }, [fetchOrderData]);

  const fetchLinkedData = useCallback(
    async (currentOrder) => {
      const jobId = currentOrder?.jobId || currentOrder?.id;
      const quoteId = currentOrder?.quoteId;
      if (!jobId) return;

      setLoadingLinked(true);
      try {
        const [invoiceRes, quoteRes] = await Promise.allSettled([
          getInvoices({ jobId }),
          quoteId ? getQuoteById(quoteId) : Promise.resolve({ success: false }),
        ]);

        if (invoiceRes.status === "fulfilled" && invoiceRes.value?.success) {
          const invList = normalizeInvoices(invoiceRes.value?.data);
          setInvoices(invList);
        } else if (invoiceRes.status === "rejected") {
          logWarn("Order dashboard: failed to load invoices", {
            jobId,
            error: invoiceRes.reason?.message || String(invoiceRes.reason),
          });
          setInvoices([]);
        }

        if (quoteRes.status === "fulfilled" && quoteRes.value?.success) {
          const q = quoteRes.value?.data;
          setQuote(q || null);

          const leadId = q?.leadId;
          if (leadId) {
            const leadRes = await getLead(leadId);
            if (leadRes?.success) {
              setLead(leadRes.data);
            } else {
              setLead(null);
            }
          } else {
            setLead(null);
          }
        } else {
          setQuote(null);
          setLead(null);
        }
      } catch (err) {
        logWarn("Order dashboard: failed to load linked data", {
          orderId: currentOrder?.id,
          error: err?.message || String(err),
        });
      } finally {
        setLoadingLinked(false);
      }
    },
    [setInvoices]
  );

  useEffect(() => {
    if (order) {
      fetchLinkedData(order);
    }
  }, [order, fetchLinkedData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [staleRes, windowRes] = await Promise.all([
          getSetting("ops.deliveries.staleUpdateHours").catch(() => null),
          getSetting("ops.deliveries.attentionWindowHours").catch(() => null),
        ]);
        const stale =
          staleRes?.data?.value ??
          staleRes?.value ??
          DEFAULT_HEALTH_THRESHOLDS.staleUpdateHours;
        const windowHrs =
          windowRes?.data?.value ??
          windowRes?.value ??
          DEFAULT_HEALTH_THRESHOLDS.attentionWindowHours;
        if (!cancelled) {
          setHealthThresholds({
            staleUpdateHours:
              Number(stale) || DEFAULT_HEALTH_THRESHOLDS.staleUpdateHours,
            attentionWindowHours:
              Number(windowHrs) ||
              DEFAULT_HEALTH_THRESHOLDS.attentionWindowHours,
          });
        }
      } catch {
        // Keep defaults
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tripId = order?.tripId;
    if (!tripId) {
      setTrip(null);
      return undefined;
    }
    (async () => {
      try {
        const data = await getTrip(tripId);
        if (!cancelled) setTrip(data || null);
      } catch (err) {
        logWarn("Order mission: failed to load trip for milestones", {
          tripId,
          error: err?.message || String(err),
        });
        if (!cancelled) setTrip(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order?.tripId]);

  const handleViewBooking = useCallback(() => {
    openAppPopup(`/bookings/${orderId}`, { title: `Booking ${orderId}` });
  }, [navigate, orderId, order]);

  const handleDownloadDriverPdf = useCallback(async () => {
    if (!orderId) {
      setNotification({
        open: true,
        message: "Order ID not available",
        severity: "error",
      });
      return;
    }
    try {
      await orderDownloadService.downloadDriverPickupPdf(orderId);
      setNotification({
        open: true,
        message: "Driver pickup PDF downloaded successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to download driver pickup PDF",
        severity: "error",
      });
    }
  }, [orderId]);

  const handleDownloadClientPdf = useCallback(async () => {
    if (!orderId) {
      setNotification({
        open: true,
        message: "Order ID not available",
        severity: "error",
      });
      return;
    }
    try {
      await orderDownloadService.downloadClientPickupPdf(orderId);
      setNotification({
        open: true,
        message: "Client pickup PDF downloaded successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to download client pickup PDF",
        severity: "error",
      });
    }
  }, [orderId]);

  // Handlers
  const handleBack = useCallback(() => {
    navigate("/orders");
  }, [navigate]);

  const handleEdit = useCallback(() => {
    navigate(`/orders/${orderId}/edit`);
  }, [navigate, orderId]);

  const handleViewIncidents = useCallback(() => {
    openAppPopup(`/orders/${orderId}/incidents`, { title: `Incidents ${orderId}` });
  }, [orderId]);

  const handleViewCompensation = useCallback(() => {
    openAppPopup(`/orders/${orderId}/compensation`, { title: `Compensation ${orderId}` });
  }, [orderId]);

  const handleViewQuote = useCallback(() => {
    const quoteId = order?.quoteId || quote?.id || quote?._id;
    if (!quoteId) return;
    openAppPopup(`/quotes/${quoteId}`, { title: `Quote ${quoteId}` });
  }, [navigate, order, quote]);

  const handleViewLead = useCallback(() => {
    const leadId = lead?.id || lead?._id || quote?.leadId;
    if (!leadId) return;
    openAppPopup(`/leads/${leadId}`, { title: `Lead ${leadId}` });
  }, [navigate, lead, quote]);

  const handleCreateInvoice = useCallback(() => {
    const jobId = order?.jobId || order?.id;
    const quoteId = order?.quoteId || quote?.id || quote?._id;
    const customerId = order?.customerId || null;
    const params = new URLSearchParams();
    if (jobId) params.set("jobId", jobId);
    if (quoteId) params.set("quoteId", quoteId);
    if (customerId) params.set("customerId", customerId);
    openAppPopup(`/invoices/new?${params.toString()}`, { title: `New Invoice ${jobId || ""}`.trim() });
  }, [navigate, order, quote]);

  const handleAppendInvoice = useCallback((invoiceId) => {
    setAddChargesModalOpen(false);
    openAppPopup(`/invoices/${invoiceId}/edit?append=1`, {
      title: `Add charges ${invoiceId}`,
    });
  }, []);

  const handleDownloadBillingStatement = useCallback(async () => {
    const jobId = order?.jobId || order?.id;
    if (!jobId) return;
    try {
      await orderDownloadService.downloadBillingStatementPdf(jobId);
      setNotification({
        open: true,
        message: "Billing statement downloaded successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to download billing statement",
        severity: "error",
      });
    }
  }, [order]);

  const handleViewLedger = useCallback(() => {
    const jobId = order?.jobId || order?.id;
    if (!jobId) return;
    navigate(`/finance/jobs/${encodeURIComponent(jobId)}/ledger`);
  }, [navigate, order]);

  const handleInvoiceLinked = useCallback(() => {
    if (order) {
      fetchLinkedData(order);
    }
    setNotification({
      open: true,
      message: "Invoice linked to this order.",
      severity: "success",
    });
  }, [order, fetchLinkedData]);

  const handleTripAssignmentChange = useCallback(
    ({ tripId }) => {
      setOrder((prev) => (prev ? { ...prev, tripId: tripId || null } : prev));
      fetchOrderData();
      setNotification({
        open: true,
        message: tripId
          ? `Order assigned to trip ${tripId}.`
          : "Order removed from trip.",
        severity: "success",
      });
    },
    [fetchOrderData]
  );

  const getQuickStatusOptions = useCallback((currentStatus) => {
    const normalized = String(currentStatus || "pending").toLowerCase();
    const nextStatuses = NEXT_STATUS_BY_CURRENT[normalized] || [];
    const options = nextStatuses.map((statusValue) => ({
      value: statusValue,
      label: QUICK_STATUS_LABELS[statusValue] || statusValue,
    }));

    if (!["cancelled", "completed"].includes(normalized)) {
      options.push({
        value: "cancelled",
        label: QUICK_STATUS_LABELS.cancelled,
        helperText: "Use when this order will not continue.",
      });
    }

    return options;
  }, []);

  const applyStatusUpdate = useCallback(
    async (newStatus, context = {}) => {
      if (!order?.id) return;

      setStatusUpdating(true);
      try {
        const updatedOrder = await updateOrderStatus(
          order.id,
          newStatus,
          context
        );
        setOrder((prev) => ({
          ...prev,
          ...(updatedOrder || {}),
          status: updatedOrder?.status || newStatus,
        }));
        setNotification({
          open: true,
          message: `Order status updated to ${String(newStatus)
            .replaceAll("_", " ")
            .toUpperCase()}`,
          severity: "success",
        });
      } catch (err) {
        setNotification({
          open: true,
          message: err.message || "Failed to update order status",
          severity: "error",
        });
      } finally {
        setStatusUpdating(false);
      }
    },
    [order]
  );

  const handleQuickStatusUpdate = useCallback(
    async (newStatus) => {
      if (!order?.id) return;
      if (newStatus === order.status) return;

      if (newStatus === "completed") {
        setStatusUpdating(true);
        try {
          const eligibility = await getOrderCompletionEligibility(order.id);
          if (eligibility && eligibility.canComplete === false) {
            setNotification({
              open: true,
              message:
                eligibility.message ||
                "Job completion blocked: resolve blockers first.",
              severity: "error",
            });
            return;
          }
          setCompleteConfirmOpen(true);
        } catch (err) {
          setNotification({
            open: true,
            message: err.message || "Failed to validate completion eligibility",
            severity: "error",
          });
        } finally {
          setStatusUpdating(false);
        }
        return;
      }

      await applyStatusUpdate(newStatus);
    },
    [order, applyStatusUpdate]
  );

  const handleCompleteConfirm = useCallback(
    async ({ includeReviewRequest }) => {
      setCompleteConfirmOpen(false);
      await applyStatusUpdate("completed", {
        includeReviewRequest: !!includeReviewRequest,
      });
    },
    [applyStatusUpdate]
  );

  const getAllStatusOptions = useCallback(() => {
    return ALL_ORDER_STATUSES.map((statusValue) => ({
      value: statusValue,
      label: QUICK_STATUS_LABELS[statusValue] || statusValue,
      disabled: statusValue === order?.status,
    }));
  }, [order?.status]);

  const handleCopyCustomerDetails = useCallback(async () => {
    const { formatCustomerDetails } = await import(
      "../../../shared/utils/customerDetailsFormatter"
    );
    const { copyToClipboard } = await import(
      "../../leads/services/communicationTemplateService"
    );
    const details = formatCustomerDetails(order);
    const success = await copyToClipboard(details);
    setNotification({
      open: true,
      message: success
        ? "Customer details copied to clipboard!"
        : "Failed to copy customer details",
      severity: success ? "success" : "error",
    });
  }, [order]);

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "in_progress":
        return "info";
      case "pending":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const customerEmail = order?.customerEmail || quote?.customerEmail || lead?.email || null;
  const customerWhatsAppLink = getWhatsAppLink(order?.customerPhone);

  const jobJourneyLinks = order
    ? seedFromOrder(order, {
        customerId: pickEntityId(
          order?.customerId,
          quote?.customerId,
          lead?.customerId
        ),
        leadId: pickEntityId(lead?.id, lead?._id, quote?.leadId),
        quoteId: pickEntityId(quote?.id, quote?._id, order?.quoteId),
        invoiceId: pickPrimaryInvoiceId(
          invoices,
          pickEntityId(quote?.id, quote?._id, order?.quoteId)
        ),
        customerDisplay:
          order?.customer ||
          order?.customerName ||
          quote?.customerName ||
          lead?.name ||
          null,
        leadDisplay: leadDisplayFrom(lead),
        quoteDisplay: quoteDisplayFrom(quote),
        invoiceDisplay: invoiceDisplayFrom(
          pickPrimaryInvoice(
            invoices,
            pickEntityId(quote?.id, quote?._id, order?.quoteId)
          )
        ),
      })
    : null;

  // Page structure shows immediately - show loading/error states inline
  return (
    <Container maxWidth="lg">
      <Box py={3}>
        {/* Error state - show inline */}
        {error && !order && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading state - show inline */}
        {loading && !order && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
            mb={3}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Not found state - show inline */}
        {!loading && !error && !order && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Order not found
            </Alert>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              variant="outlined"
            >
              Back to Orders
            </Button>
          </Box>
        )}

        {/* Mission Header */}
        {order && (
          <OrderCommandHeader
            order={order}
            trip={trip}
            invoicesRollup={computeInvoiceRollup(invoices)}
            healthThresholds={healthThresholds}
            hasBooking={hasBooking}
            onBack={handleBack}
            onUpdateStatus={() => navigate(`/orders/${orderId}/edit?focus=status`)}
            quickStatusOptions={getQuickStatusOptions(order?.status)}
            allStatusOptions={getAllStatusOptions()}
            onQuickStatusSelect={handleQuickStatusUpdate}
            statusUpdating={statusUpdating}
            onEdit={handleEdit}
            onViewBooking={handleViewBooking}
            onViewIncidents={handleViewIncidents}
            onViewCompensation={handleViewCompensation}
            onDownloadDriverPdf={handleDownloadDriverPdf}
            onDownloadClientPdf={handleDownloadClientPdf}
          />
        )}

        {jobJourneyLinks ? (
          <JobJourneyStrip
            current="order"
            customerId={jobJourneyLinks.customerId}
            leadId={jobJourneyLinks.leadId}
            quoteId={jobJourneyLinks.quoteId}
            invoiceId={jobJourneyLinks.invoiceId}
            orderId={jobJourneyLinks.orderId}
            customerDisplay={jobJourneyLinks.customerDisplay}
            leadDisplay={jobJourneyLinks.leadDisplay}
            quoteDisplay={jobJourneyLinks.quoteDisplay}
            invoiceDisplay={jobJourneyLinks.invoiceDisplay}
            orderDisplay={jobJourneyLinks.orderDisplay}
          />
        ) : null}

        {order ? (
          <OrderJourneyBar order={order} trip={trip} />
        ) : null}

        {/* Show content only when order is loaded */}
        {order ? (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Operational flow
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowOperationalFlow((prev) => !prev)}
                startIcon={showOperationalFlow ? <ExpandLess /> : <ExpandMore />}
                sx={{ textTransform: "none" }}
              >
                {showOperationalFlow ? "Hide flow details" : "Show flow details"}
              </Button>
            </Box>

            {showOperationalFlow ? (
              <OrderLinkedRecordsStrip
                order={order}
                quote={quote}
                lead={lead}
                invoicesRollup={computeInvoiceRollup(invoices)}
                onOpenLead={handleViewLead}
                onOpenQuote={handleViewQuote}
                onOpenInvoices={() =>
                  openAppPopup(`/invoices?jobId=${encodeURIComponent(order.jobId || order.id)}`, {
                    title: `Invoices ${order.jobId || order.id}`,
                  })
                }
                onOpenBooking={() => setActiveTab(1)}
                onOpenOperations={() => setActiveTab(1)}
                onOpenLedger={handleViewLedger}
              />
            ) : null}

            <MarketplaceCohortOpsBanner orderId={orderId} quote={quote} />

            <ProviderInvitationPanel
              orderId={orderId}
              onInvited={() => setProviderOpsRefresh((n) => n + 1)}
            />

            <ProviderSelectionPanel
              key={providerOpsRefresh}
              orderId={orderId}
              order={order}
              onAssigned={fetchOrderData}
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <OrderMissionWorkspaces
                  order={order}
                  orderId={orderId}
                  trip={trip}
                  quote={quote}
                  lead={lead}
                  invoices={invoices}
                  loadingLinked={loadingLinked}
                  hasBooking={hasBooking}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  navigate={navigate}
                  canLinkInvoice={canLinkInvoice}
                  canManageInvoices={canManageInvoices}
                  canViewInvoices={canViewInvoices}
                  customerEmail={customerEmail}
                  customerWhatsAppLink={customerWhatsAppLink}
                  getStatusColor={getStatusColor}
                  handleCopyCustomerDetails={handleCopyCustomerDetails}
                  handleViewBooking={handleViewBooking}
                  handleViewLedger={handleViewLedger}
                  handleCreateInvoice={handleCreateInvoice}
                  onAddCharges={() => setAddChargesModalOpen(true)}
                  onDownloadBillingStatement={handleDownloadBillingStatement}
                  setLinkInvoiceModalOpen={setLinkInvoiceModalOpen}
                  handleTripAssignmentChange={handleTripAssignmentChange}
                  handleDownloadDriverPdf={handleDownloadDriverPdf}
                  handleDownloadClientPdf={handleDownloadClientPdf}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <OrderAttentionRail
                  order={order}
                  trip={trip}
                  quote={quote}
                  invoicesRollup={computeInvoiceRollup(invoices)}
                  healthThresholds={healthThresholds}
                  hasBooking={hasBooking}
                  onOpenInvoices={() =>
                    openAppPopup(
                      `/invoices?jobId=${encodeURIComponent(order.jobId || order.id)}`,
                      { title: `Invoices ${order.jobId || order.id}` }
                    )
                  }
                  onOpenQuote={handleViewQuote}
                  onOpenBooking={handleViewBooking}
                  onJumpToActivity={() => setActiveTab(5)}
                  onJumpToTracking={() => setActiveTab(2)}
                  onJumpToFinance={() => setActiveTab(4)}
                  onJumpToOperations={() => setActiveTab(1)}
                />
              </Grid>
            </Grid>
          </>
        ) : null}

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setNotification({ ...notification, open: false })}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>

        <LinkInvoiceToOrderModal
          open={linkInvoiceModalOpen}
          onClose={() => setLinkInvoiceModalOpen(false)}
          orderId={orderId}
          onLinked={handleInvoiceLinked}
        />

        <AddOrderChargesModal
          open={addChargesModalOpen}
          onClose={() => setAddChargesModalOpen(false)}
          invoices={invoices}
          onCreateInvoice={() => {
            setAddChargesModalOpen(false);
            handleCreateInvoice();
          }}
          onAppendToInvoice={handleAppendInvoice}
        />

        <CompleteOrderConfirmModal
          open={completeConfirmOpen}
          orderId={order?.id}
          loading={statusUpdating}
          onConfirm={handleCompleteConfirm}
          onCancel={() => setCompleteConfirmOpen(false)}
        />
      </Box>
    </Container>
  );
};

export default OrderDetailsView;
